"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShoppingBag, Layers, Search, ArrowUpRight, SlidersHorizontal, CircleCheck as CheckCircle2, ShieldCheck, TrendingUp, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const corePillars = [{
  icon: Layers,
  title: "Category & collection architecture",
  summary: "Most store sales start on collection hubs, not individual products. I structure your hierarchy around how customers naturally filter and search.",
  points: ["Target high-intent transactional search terms", "Eliminate keyword cannibalisation across nested tags", "Write unique, customer-centric introductory guidance"]
}, {
  icon: SlidersHorizontal,
  title: "Faceted navigation & crawl traps",
  summary: "Filters for size, colour, and price easily generate millions of indexable duplicates. I audit and configure canonicals, robots rules, and parameter handling.",
  points: ["Prevent index bloat and wasted crawl budget", "Clean canonical tags across platform variations", "Consolidate variant URLs into primary SKUs"]
}, {
  icon: ShoppingBag,
  title: "High-converting product detail pages",
  summary: "Getting visitors to a product page is only half the job. I ensure your pages rank for specific SKUs and guide shoppers toward buying.",
  points: ["Accurate Schema.org Product & Offer markup", "Genuine customer review and rating signals", "Fast Core Web Vitals on mobile checkout paths"]
}, {
  icon: TrendingUp,
  title: "Revenue-focused measurement",
  summary: "I do not measure success by empty organic impressions. You and I monitor bottom-line organic revenue, basket size, and high-margin product growth.",
  points: ["Clear Google Search Console segmenting", "Transparent monthly reporting on conversions", "Direct guidance on inventory seasonality"]
}];
const platformExpertise = [{
  name: "Shopify & Shopify Plus",
  detail: "Resolving /collections/ vs /products/ duplicate URL paths, liquid template bloat, and app script loading."
}, {
  name: "WooCommerce & WordPress",
  detail: "Optimising database queries, category breadcrumbs, caching rules, and custom product taxonomies."
}, {
  name: "Magento / Adobe Commerce",
  detail: "Taming massive faceted indexes, attribute set conflicts, and slow catalog page generation."
}, {
  name: "Custom & Headless Stacks",
  detail: "Ensuring server-side rendering for JavaScript storefronts, hydration speed, and clean crawlable routes."
}];
export default function Ecommerce() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="ecommerceseostrategy" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      {/* Subtle decorative background tints */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute top-12 left-1/2 -translate-x-1/2 w-[1000px] h-[400px] bg-secondary/40 blur-3xl rounded-full opacity-60" />
        <div className="absolute bottom-10 right-0 w-[450px] h-[350px] bg-accent/10 blur-3xl rounded-full opacity-50" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {/* Header Block */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="max-w-3xl mb-16 md:mb-20">
          <motion.div variants={itemVariants} className="flex items-center gap-2 mb-4">
            <Badge variant="outline" className="border-primary/20 bg-primary/5 text-primary gap-1.5 px-3 py-1 font-medium text-xs">
              <Sparkles className="size-3.5 text-accent" />
              Direct Strategic Guidance
            </Badge>
            <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
              E-commerce & Retail SEO
            </span>
          </motion.div>

          <motion.h2 variants={itemVariants} className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground font-default leading-tight mb-6">
            How I help retail stores turn organic search into steady orders.
          </motion.h2>

          <motion.p variants={itemVariants} className="text-lg text-muted-foreground leading-relaxed font-default">
            From faceted crawl bloat and URL parameter duplication to structured schema and collection hierarchy, I resolve the technical ecommerce bottlenecks that suppress your store's search performance.
          </motion.p>
        </motion.div>

        {/* 4 Pillars Grid */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-16">
          {corePillars.map((pillar, i) => {
          const IconComponent = pillar.icon;
          return <motion.div key={pillar.title} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border/80 bg-card hover:border-primary/40 shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-300 rounded-xl">
                  <CardHeader className="pb-4">
                    <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                      <IconComponent className="size-6" />
                    </div>
                    <CardTitle className="text-xl font-semibold text-foreground tracking-tight font-default">
                      {pillar.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 pt-0">
                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                      {pillar.summary}
                    </p>
                    <Separator className="bg-border/60" />
                    <ul className="space-y-2.5 pt-1">
                      {pillar.points.map(point => <li key={point} className="flex items-start gap-2.5 text-sm text-foreground">
                          <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                          <span>{point}</span>
                        </li>)}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Platform Technical Matrix & Personal Promise Card */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="rounded-2xl border border-border bg-card p-6 sm:p-10 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 text-primary font-medium text-sm">
                <Search className="size-4" />
                <span>Storefront Architecture & Platforms</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight font-default">
                Tailored solutions for your specific ecommerce platform.
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                Different ecommerce engines introduce unique SEO constraints. I diagnose the code-level hurdles native to your specific software rather than handing you generic advice.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                {platformExpertise.map((item, i) => <div key={item.name} data-index={i} className="p-4 rounded-lg bg-background border border-border/70 text-left">
                    <h4 className="text-sm font-semibold text-foreground mb-1.5 flex items-center gap-1.5">
                      <span className="size-1.5 rounded-full bg-accent" />
                      {item.name}
                    </h4>
                    <p className="text-xs text-muted-foreground leading-normal">
                      {item.detail}
                    </p>
                  </div>)}
              </div>
            </div>

            <div className="lg:col-span-5 bg-background border border-border/90 rounded-xl p-6 sm:p-8 space-y-6">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <ShieldCheck className="size-5" />
                </div>
                <div>
                  <h4 className="text-base font-semibold text-foreground">Hands-on technical ecommerce SEO</h4>
                  <p className="text-xs text-muted-foreground">Led by Yassine Essaadi</p>
                </div>
              </div>

              <p className="text-sm text-foreground/90 leading-relaxed">
                {"\"When you work with me, I personally inspect your store's crawl logs, audit your category taxonomy, and write every recommendation. You get honest strategic priority, not endless junior agency hours.\""}
              </p>

              <div className="space-y-3 pt-2">
                <Button asChild size="lg" className="w-full justify-between group">
                  <Link to="/ContactPage">
                    <span>Book a Free SEO Consultation</span>
                    <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="w-full">
                  <Link to="/CaseStudiesPage">
                    View verified case studies
                  </Link>
                </Button>
              </div>

              <div className="pt-2">
                <p className="text-xs text-muted-foreground text-center">
                  UK & International stores | No long-term lock-ins
                </p>
              </div>
            </div>

          </div>
        </motion.div>

      </div>
    </section>;
}